import { Loadable } from '@/components/loaders';
import { useWorkspace } from '@/features/workspaces/hooks';
import { DataSourceIDEnum } from '@/models/dataSource';
import { useCompanyValues } from '@/providers';
import {
  usePrepareConnectorFlowQuery,
  usePrepareConnectorFlowStepMutation,
} from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { useSaveDataSourceMutation } from '@/services/dataSources';
import { FormikProps } from 'formik';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnItem } from '../../../../types';
import {
  buildPreviewRowsFromFieldRecords,
  ColumnMapEditor,
  getHeadersFromColumnMap,
} from '../../../commons/ColumnMapEditor';
import { SOURCE_CONNECTOR_WIZARD_DIRECTION } from '../constants';
import {
  extractMappingHeadersFromPrepareStepResponse,
  getMappingPrepareStepId,
} from '../sourceConnectorFlowForms';
import { SourceConnectorWizardData } from '../types';

type SourceConnectorColumnMapStepProps = {
  wizardData: SourceConnectorWizardData;
  dataSourceTypeId: DataSourceIDEnum;
  onNext: (data: Partial<SourceConnectorWizardData>) => void;
  onPrev: () => void;
  onCancel: () => void;
};

export const SourceConnectorColumnMapStep: FC<SourceConnectorColumnMapStepProps> = ({
  wizardData,
  dataSourceTypeId,
  onNext,
  onPrev,
}) => {
  const { t } = useTranslation('data_sources');
  const workspace = useWorkspace();
  const { currentLanguage } = useCompanyValues();
  const [saveDataSource] = useSaveDataSourceMutation();
  const [prepareMappingStep, { isLoading: isPreparingMapping }] = usePrepareConnectorFlowStepMutation();
  const columnMapFormRef = useRef<FormikProps<ColumnItem[]>>(null);
  const [mappingHeaders, setMappingHeaders] = useState<string[]>(wizardData.mappingHeaders);
  const [prepareError, setPrepareError] = useState<string | null>(null);

  const flowId = wizardData.flow?.id ?? '';

  const { data: preparedFlow, isLoading: isLoadingPreparedFlow } = usePrepareConnectorFlowQuery(
    { workspaceId: workspace.id, id: flowId },
    { skip: !flowId }
  );

  const mappingStepId = useMemo(() => {
    if (!preparedFlow) return null;
    const translated = translateI18nValues(preparedFlow, currentLanguage);
    return getMappingPrepareStepId(translated);
  }, [preparedFlow, currentLanguage]);

  useEffect(() => {
    const loadMappingStep = async () => {
      if (!flowId || !mappingStepId || mappingHeaders.length > 0) return;

      setPrepareError(null);
      try {
        const response = await prepareMappingStep({
          workspaceId: workspace.id,
          flowId,
          stepId: mappingStepId,
          stepData: wizardData.params,
          direction: SOURCE_CONNECTOR_WIZARD_DIRECTION,
        }).unwrap();

        const translated = translateI18nValues(response, currentLanguage);
        const headers = extractMappingHeadersFromPrepareStepResponse(translated, mappingStepId);

        if (headers.length === 0) {
          const previewKeys = Array.from(
            new Set(wizardData.previewRecords.flatMap((record) => Object.keys(record.fields)))
          );
          setMappingHeaders(previewKeys);
        } else {
          setMappingHeaders(headers);
        }
      } catch (error: unknown) {
        const message =
          (error as { data?: { message?: string } })?.data?.message ?? t('wizard.source_connector.mapping_prepare_failed');
        setPrepareError(message);
      }
    };

    loadMappingStep();
  }, [
    currentLanguage,
    flowId,
    mappingHeaders.length,
    mappingStepId,
    prepareMappingStep,
    t,
    wizardData.params,
    wizardData.previewRecords,
    workspace.id,
  ]);

  const previewRows = useMemo(
    () => buildPreviewRowsFromFieldRecords(mappingHeaders, wizardData.previewRecords.map((r) => r.fields)),
    [mappingHeaders, wizardData.previewRecords]
  );

  const handleConfirm = async (mappings: ReturnType<typeof getHeadersFromColumnMap>) => {
    if (!wizardData.connectorInstance || !wizardData.flow) return;

    const response = await saveDataSource({
      workspaceId: workspace.id,
      dataSource: {
        name: wizardData.name,
        type: dataSourceTypeId,
        configuration: {},
        connectorInstanceId: wizardData.connectorInstance.id,
        connectorFlowId: wizardData.flow.id,
        params: wizardData.params,
        mappings,
      },
    });

    if ('data' in response && response.data?.dataSource) {
      onNext({ mappings, mappingHeaders, createdDataSource: response.data.dataSource });
    }
  };

  const isLoading = isLoadingPreparedFlow || isPreparingMapping;

  return (
    <Loadable isLoading={isLoading}>
      {prepareError && <p className='mb-4 text-sm text-red-600'>{prepareError}</p>}

      {mappingHeaders.length > 0 && !prepareError && (
        <ColumnMapEditor
          workspaceId={workspace.id}
          headers={mappingHeaders}
          previewRows={previewRows}
          formRef={columnMapFormRef}
          onConfirm={handleConfirm}
          onPrev={onPrev}
        />
      )}

      {!isLoading && mappingHeaders.length === 0 && !prepareError && (
        <p className='text-sm text-secondary-600'>{t('wizard.source_connector.mapping_fields_unavailable')}</p>
      )}
    </Loadable>
  );
};
